/**
 * Zone Map Component
 * Map with Mapbox GL Draw for professional polygon drawing and editing
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import { cn } from '../../utils/cn';
import type { Zone } from '../../types';
import {
  Pencil,
  MousePointer2,
  Move,
  Trash2,
  RotateCcw,
  Check,
  X,
  Maximize2,
  Info,
} from 'lucide-react';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

interface ZoneMapProps {
  zones: Zone[];
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string) => void;
  onPolygonDrawn?: (coordinates: number[][][]) => void;
  isDrawingMode?: boolean;
  editingZone?: Zone | null;
  className?: string;
}

interface PolygonStats {
  area: number; // in square kilometers
  perimeter: number; // in kilometers
  vertexCount: number;
  isValid: boolean;
  centroid: [number, number] | null;
}

// Default center (Baghdad)
const DEFAULT_CENTER: [number, number] = [44.366, 33.315];
const DEFAULT_ZOOM = 11;

// Custom draw styles for better UX
const drawStyles = [
  // Polygon fill - active (being drawn/edited)
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': '#3B82F6',
      'fill-opacity': 0.2,
    },
  },
  // Polygon fill - inactive
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': '#3B82F6',
      'fill-opacity': 0.15,
    },
  },
  // Polygon outline - active
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: {
      'line-color': '#3B82F6',
      'line-width': 3,
    },
  },
  // Polygon outline - inactive
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
    paint: {
      'line-color': '#3B82F6',
      'line-width': 2,
      'line-dasharray': [2, 2],
    },
  },
  // Vertex points - active
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 7,
      'circle-color': '#FFFFFF',
      'circle-stroke-color': '#3B82F6',
      'circle-stroke-width': 2,
    },
  },
  // Midpoints (for adding new vertices)
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#3B82F6',
      'circle-opacity': 0.7,
    },
  },
  // Line being drawn
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    paint: {
      'line-color': '#3B82F6',
      'line-width': 2,
      'line-dasharray': [2, 2],
    },
  },
  // Points
  {
    id: 'gl-draw-point-active',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'active', 'true']],
    paint: {
      'circle-radius': 6,
      'circle-color': '#3B82F6',
    },
  },
];

export default function ZoneMap({
  zones,
  selectedZoneId,
  onZoneSelect,
  onPolygonDrawn,
  isDrawingMode = false,
  editingZone,
  className,
}: ZoneMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const draw = useRef<MapboxDraw | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentMode, setCurrentMode] = useState<string>('simple_select');
  const [polygonStats, setPolygonStats] = useState<PolygonStats | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const prevZonesRef = useRef<Zone[]>([]);

  // Calculate polygon statistics using Turf.js
  const calculatePolygonStats = useCallback((coordinates: number[][][]): PolygonStats | null => {
    try {
      if (!coordinates || coordinates.length === 0 || coordinates[0].length < 4) {
        return null;
      }

      const polygon = turf.polygon(coordinates);
      const area = turf.area(polygon) / 1_000_000; // Convert to km²
      const perimeter = turf.length(turf.polygonToLine(polygon), { units: 'kilometers' });
      const centroidPoint = turf.centroid(polygon);
      const centroid = centroidPoint.geometry.coordinates as [number, number];
      
      // Check if polygon is valid (no self-intersections)
      const isValid = !turf.kinks(polygon).features.length;

      return {
        area: Math.round(area * 100) / 100,
        perimeter: Math.round(perimeter * 100) / 100,
        vertexCount: coordinates[0].length - 1, // Exclude closing point
        isValid,
        centroid,
      };
    } catch {
      return null;
    }
  }, []);

  // Update polygon stats from draw features
  const updatePolygonStats = useCallback(() => {
    if (!draw.current) return;

    const features = draw.current.getAll();
    if (features.features.length > 0) {
      const feature = features.features[0];
      if (feature.geometry.type === 'Polygon') {
        const stats = calculatePolygonStats(feature.geometry.coordinates as number[][][]);
        setPolygonStats(stats);
      }
    } else {
      setPolygonStats(null);
    }
  }, [calculatePolygonStats]);

  // Initialize map and draw control
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      doubleClickZoom: false,
    });

    // Initialize Mapbox Draw
    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      styles: drawStyles,
      defaultMode: 'simple_select',
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(draw.current as unknown as mapboxgl.IControl, 'top-left');

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    // Draw event listeners
    map.current.on('draw.create', () => {
      updatePolygonStats();
    });

    map.current.on('draw.update', () => {
      updatePolygonStats();
    });

    map.current.on('draw.delete', () => {
      setPolygonStats(null);
    });

    map.current.on('draw.modechange', (e: { mode: string }) => {
      setCurrentMode(e.mode);
    });

    map.current.on('draw.selectionchange', () => {
      updatePolygonStats();
    });

    return () => {
      map.current?.remove();
      map.current = null;
      draw.current = null;
    };
  }, [updatePolygonStats]);

  // Handle drawing mode changes
  useEffect(() => {
    if (!draw.current || !mapLoaded) return;

    if (isDrawingMode) {
      // Clear existing drawings and start fresh
      draw.current.deleteAll();
      // eslint-disable-next-line react-hooks/set-state-in-effect -- this effect syncs the mapbox-gl-draw instance; the stats mirror the draw features it just cleared
      setPolygonStats(null);

      // If editing an existing zone, load its polygon
      if (editingZone) {
        const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
          type: 'Feature',
          properties: {},
          geometry: editingZone.polygon,
        };
        draw.current.add(feature);
        draw.current.changeMode('direct_select', {
          featureId: draw.current.getAll().features[0]?.id as string,
        });
        setCurrentMode('direct_select');
        updatePolygonStats();
      } else {
        // Start drawing a new polygon
        draw.current.changeMode('draw_polygon');
        setCurrentMode('draw_polygon');
      }
    } else {
      // Exit drawing mode
      draw.current.changeMode('simple_select');
      setCurrentMode('simple_select');
    }
  }, [isDrawingMode, editingZone, mapLoaded, updatePolygonStats]);

  // Drawing tool actions
  const handleDrawPolygon = useCallback(() => {
    if (!draw.current) return;
    draw.current.deleteAll();
    draw.current.changeMode('draw_polygon');
    setCurrentMode('draw_polygon');
    setPolygonStats(null);
  }, []);

  const handleDirectSelect = useCallback(() => {
    if (!draw.current) return;
    const features = draw.current.getAll();
    if (features.features.length > 0) {
      draw.current.changeMode('direct_select', {
        featureId: features.features[0].id as string,
      });
      setCurrentMode('direct_select');
    }
  }, []);

  const handleSimpleSelect = useCallback(() => {
    if (!draw.current) return;
    draw.current.changeMode('simple_select');
    setCurrentMode('simple_select');
  }, []);

  const handleDeleteAll = useCallback(() => {
    if (!draw.current) return;
    draw.current.deleteAll();
    draw.current.changeMode('draw_polygon');
    setCurrentMode('draw_polygon');
    setPolygonStats(null);
  }, []);

  const handleUndo = useCallback(() => {
    if (!draw.current) return;
    const features = draw.current.getAll();
    if (features.features.length > 0) {
      const feature = features.features[0];
      if (feature.geometry.type === 'Polygon') {
        const coords = feature.geometry.coordinates[0];
        if (coords.length > 4) {
          // Remove the second-to-last point (last point is closing point)
          const newCoords = [...coords.slice(0, -2), coords[coords.length - 1]];
          feature.geometry.coordinates = [newCoords];
          draw.current.delete(feature.id as string);
          draw.current.add(feature);
          draw.current.changeMode('direct_select', {
            featureId: draw.current.getAll().features[0]?.id as string,
          });
          updatePolygonStats();
        }
      }
    }
  }, [updatePolygonStats]);

  const handleFinishDrawing = useCallback(() => {
    if (!draw.current) return;

    const features = draw.current.getAll();
    if (features.features.length > 0) {
      const feature = features.features[0];
      if (feature.geometry.type === 'Polygon') {
        const coordinates = feature.geometry.coordinates as number[][][];
        
        // Validate polygon
        const stats = calculatePolygonStats(coordinates);
        if (!stats?.isValid) {
          alert('Invalid polygon: The polygon has self-intersections. Please fix the shape.');
          return;
        }

        onPolygonDrawn?.(coordinates);
      }
    }
  }, [onPolygonDrawn, calculatePolygonStats]);

  const handleCancelDrawing = useCallback(() => {
    if (!draw.current) return;
    draw.current.deleteAll();
    draw.current.changeMode('simple_select');
    setCurrentMode('simple_select');
    setPolygonStats(null);
  }, []);

  const handleFitToPolygon = useCallback(() => {
    if (!draw.current || !map.current) return;

    const features = draw.current.getAll();
    if (features.features.length > 0) {
      const feature = features.features[0];
      if (feature.geometry.type === 'Polygon') {
        const bbox = turf.bbox(feature);
        map.current.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
          { padding: 100, maxZoom: 15 }
        );
      }
    }
  }, []);

  // Render zone polygons (existing zones)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove old zone layers
    prevZonesRef.current.forEach((zone) => {
      const sourceId = `zone-${zone._id}`;
      if (map.current?.getSource(sourceId)) {
        try {
          map.current.removeLayer(`zone-fill-${zone._id}`);
          map.current.removeLayer(`zone-outline-${zone._id}`);
          map.current.removeSource(sourceId);
        } catch {
          // Layer might not exist
        }
      }
    });

    // Add zone layers
    zones.forEach((zone) => {
      // Skip the zone being edited (it's in the draw control)
      if (editingZone?._id === zone._id) return;

      const sourceId = `zone-${zone._id}`;
      const isSelected = zone._id === selectedZoneId;

      // Check if source already exists
      if (map.current?.getSource(sourceId)) return;

      map.current!.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: { id: zone._id, name: zone.name },
          geometry: zone.polygon,
        },
      });

      map.current!.addLayer({
        id: `zone-fill-${zone._id}`,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': zone.color,
          'fill-opacity': isSelected ? 0.4 : 0.2,
        },
      });

      map.current!.addLayer({
        id: `zone-outline-${zone._id}`,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': zone.color,
          'line-width': isSelected ? 3 : 2,
          'line-opacity': isSelected ? 1 : 0.7,
        },
      });

      // Click handler (only when not drawing)
      if (!isDrawingMode) {
        map.current!.on('click', `zone-fill-${zone._id}`, () => {
          onZoneSelect?.(zone._id);
        });

        map.current!.on('mouseenter', `zone-fill-${zone._id}`, () => {
          if (map.current) map.current.getCanvas().style.cursor = 'pointer';
        });

        map.current!.on('mouseleave', `zone-fill-${zone._id}`, () => {
          if (map.current) map.current.getCanvas().style.cursor = '';
        });
      }
    });

    prevZonesRef.current = zones;

    // Fit bounds to all zones
    if (zones.length > 0 && !editingZone && !isDrawingMode) {
      const bounds = new mapboxgl.LngLatBounds();
      zones.forEach((zone) => {
        zone.polygon.coordinates[0].forEach((coord) => {
          bounds.extend(coord as [number, number]);
        });
      });
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 13 });
    }
  }, [zones, selectedZoneId, editingZone, mapLoaded, onZoneSelect, isDrawingMode]);

  if (!mapboxgl.accessToken) {
    return (
      <div
        className={cn('bg-gray-100 rounded-lg flex items-center justify-center', className)}
        style={{ minHeight: '400px' }}
      >
        <div className="text-center text-gray-500">
          <p className="font-medium">Map not available</p>
          <p className="text-sm">Set VITE_MAPBOX_TOKEN to enable map view</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={mapContainer}
        className={cn('rounded-lg overflow-hidden', className)}
        style={{ minHeight: '400px' }}
      />

      {/* Drawing Tools Panel */}
      {isDrawingMode && (
        <div className="absolute top-4 left-14 z-10 flex flex-col gap-2">
          {/* Mode Indicator */}
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2">
            <span>
              {currentMode === 'draw_polygon' && 'Drawing Mode: Click to add points'}
              {currentMode === 'direct_select' && 'Edit Mode: Drag vertices to adjust'}
              {currentMode === 'simple_select' && 'Select Mode: Click polygon to edit'}
            </span>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="ml-2 p-1 hover:bg-blue-500 rounded"
              title="Show help"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>

          {/* Help Panel */}
          {showHelp && (
            <div className="bg-white rounded-lg shadow-lg p-3 text-sm max-w-xs">
              <h4 className="font-semibold mb-2">Drawing Tips:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• <strong>Draw:</strong> Click to add vertices</li>
                <li>• <strong>Complete:</strong> Click first point or double-click</li>
                <li>• <strong>Edit:</strong> Drag vertices to move them</li>
                <li>• <strong>Add vertex:</strong> Click midpoints (small dots)</li>
                <li>• <strong>Delete vertex:</strong> Select and press Delete</li>
                <li>• <strong>Move polygon:</strong> Use simple select mode</li>
              </ul>
            </div>
          )}

          {/* Drawing Tools */}
          <div className="bg-white rounded-lg shadow-lg p-2 flex flex-col gap-1">
            <button
              onClick={handleDrawPolygon}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors',
                currentMode === 'draw_polygon'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100 text-gray-700'
              )}
              title="Draw new polygon"
            >
              <Pencil className="h-4 w-4" />
              Draw
            </button>

            <button
              onClick={handleDirectSelect}
              disabled={!polygonStats}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors',
                currentMode === 'direct_select'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100 text-gray-700',
                !polygonStats && 'opacity-50 cursor-not-allowed'
              )}
              title="Edit vertices"
            >
              <MousePointer2 className="h-4 w-4" />
              Edit Vertices
            </button>

            <button
              onClick={handleSimpleSelect}
              disabled={!polygonStats}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors',
                currentMode === 'simple_select'
                  ? 'bg-blue-100 text-blue-700'
                  : 'hover:bg-gray-100 text-gray-700',
                !polygonStats && 'opacity-50 cursor-not-allowed'
              )}
              title="Move polygon"
            >
              <Move className="h-4 w-4" />
              Move
            </button>

            <div className="border-t border-gray-200 my-1" />

            <button
              onClick={handleUndo}
              disabled={!polygonStats || (polygonStats.vertexCount <= 3)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors hover:bg-gray-100 text-gray-700',
                (!polygonStats || polygonStats.vertexCount <= 3) && 'opacity-50 cursor-not-allowed'
              )}
              title="Remove last vertex"
            >
              <RotateCcw className="h-4 w-4" />
              Undo Point
            </button>

            <button
              onClick={handleFitToPolygon}
              disabled={!polygonStats}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors hover:bg-gray-100 text-gray-700',
                !polygonStats && 'opacity-50 cursor-not-allowed'
              )}
              title="Fit map to polygon"
            >
              <Maximize2 className="h-4 w-4" />
              Fit to View
            </button>

            <button
              onClick={handleDeleteAll}
              className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors hover:bg-red-50 text-red-600"
              title="Delete and start over"
            >
              <Trash2 className="h-4 w-4" />
              Clear
            </button>
          </div>

          {/* Polygon Stats */}
          {polygonStats && (
            <div className="bg-white rounded-lg shadow-lg p-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Zone Statistics</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Area:</span>
                  <span className="font-medium">{polygonStats.area} km²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Perimeter:</span>
                  <span className="font-medium">{polygonStats.perimeter} km</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Vertices:</span>
                  <span className="font-medium">{polygonStats.vertexCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Valid:</span>
                  <span className={cn(
                    'font-medium',
                    polygonStats.isValid ? 'text-green-600' : 'text-red-600'
                  )}>
                    {polygonStats.isValid ? 'Yes' : 'No (self-intersecting)'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {polygonStats && (
            <div className="flex gap-2">
              <button
                onClick={handleFinishDrawing}
                disabled={!polygonStats.isValid}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-colors',
                  polygonStats.isValid
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                )}
              >
                <Check className="h-4 w-4" />
                Confirm
              </button>
              <button
                onClick={handleCancelDrawing}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
